import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { formatIdfySelfieErrorMessage } from '@services/idfyKyc/idfyKycApi';

export type FaceMatchUiStatus = 'idle' | 'approved' | 'rejected';

export interface KycSelfieVerificationPanelProps {
	disabled: boolean;
	isProfileUploading: boolean;
	isVerifyingFace: boolean;
	faceMatchStatus: FaceMatchUiStatus;
	faceMatchMessage: string;
	/** Shown in the oval when not using the live camera (captured image / server URL). */
	selfiePreviewUrl: string | null;
	onSelfieFile: (file: File) => void;
	/** Clears rejected state and preview so the user can capture again. */
	onRetakeSelfie?: () => void;
	/** Allows the user to go back to document upload to replace the document. */
	onChangeDocument?: () => void;
	onCameraError?: () => void;
}

const ShieldIcon = (): ReactElement => (
	<svg className="kyc-selfie-panel__shield" viewBox="0 0 24 24" width="22" height="22" aria-hidden>
		<path
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinejoin="round"
			d="M12 3.5l7 3.2v5.2c0 4.5-3 8.7-7 9.6-4-1-7-5.1-7-9.6V6.7l7-3.2z"
		/>
	</svg>
);

const SuccessCheckIcon = (): ReactElement => (
	<span className="kyc-selfie-panel__success-icon" aria-hidden>
		<svg viewBox="0 0 48 48" width="48" height="48" focusable="false">
			<circle cx="24" cy="24" r="22" fill="#2e7d32" />
			<path
				d="M14 24l8 8 12-14"
				fill="none"
				stroke="#fff"
				strokeWidth="3"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	</span>
);

export const KycSelfieVerificationPanel = (props: KycSelfieVerificationPanelProps): ReactElement => {
	const {
		disabled,
		isProfileUploading,
		isVerifyingFace,
		faceMatchStatus,
		faceMatchMessage,
		selfiePreviewUrl,
		onSelfieFile,
		onRetakeSelfie,
		onChangeDocument,
		onCameraError,
	} = props;
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [cameraLoading, setCameraLoading] = useState(false);

	useEffect(() => {
		return (): void => {
			setStream((prev) => {
				prev?.getTracks().forEach((t) => t.stop());
				return null;
			});
		};
	}, []);

	useEffect(() => {
		const video = videoRef.current;
		if (!stream || !video) {
			return;
		}
		video.srcObject = stream;
		void video.play().catch(() => undefined);
		return (): void => {
			video.srcObject = null;
		};
	}, [stream]);

	const requestCamera = useCallback(async (): Promise<void> => {
		setStream((prev) => {
			prev?.getTracks().forEach((t) => t.stop());
			return null;
		});
		setCameraLoading(true);
		try {
			const next = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: 'user' } },
				audio: false,
			});
			setStream(next);
		} catch {
			onCameraError?.();
		} finally {
			setCameraLoading(false);
		}
	}, [onCameraError]);

	const captureAndUpload = useCallback((): void => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (!video || !canvas || video.readyState < 2) {
			return;
		}
		const srcW = video.videoWidth;
		const srcH = video.videoHeight;
		if (!srcW || !srcH) {
			return;
		}
		canvas.width = srcW;
		canvas.height = srcH;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return;
		}
		ctx.drawImage(video, 0, 0, srcW, srcH);
		canvas.toBlob(
			(blob) => {
				if (!blob) {
					return;
				}
				const file = new File([blob], `kyc-selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
				setStream((prev) => {
					prev?.getTracks().forEach((t) => t.stop());
					return null;
				});
				onSelfieFile(file);
			},
			'image/jpeg',
			0.92
		);
	}, [onSelfieFile]);

	const busy = disabled || isProfileUploading || isVerifyingFace;
	const isSuccess = faceMatchStatus === 'approved';
	const isRejected = faceMatchStatus === 'rejected';
	const showPermission = !stream && !selfiePreviewUrl;
	const showLiveVideo = Boolean(stream);
	const showStaticSelfie = Boolean(selfiePreviewUrl) && !showLiveVideo;

	const rejectionDisplay = useMemo(
		() => formatIdfySelfieErrorMessage(faceMatchMessage) || faceMatchMessage,
		[faceMatchMessage]
	);

	if (isSuccess) {
		return (
			<div className="kyc-selfie-panel kyc-selfie-panel--success">
				<div className="kyc-selfie-panel__success-head">
					<SuccessCheckIcon />
					<div className="kyc-selfie-panel__success-copy">
						<h3 className="kyc-selfie-panel__success-title">Verification Successful</h3>
						<p className="kyc-selfie-panel__success-sub">
							Your identity has been successfully verified. You now have full access to your account and all features.
						</p>
					</div>
				</div>
				<div className="kyc-selfie-panel__stage">
					<div className="kyc-selfie-panel__oval kyc-selfie-panel__oval--static">
						{selfiePreviewUrl ? (
							<img src={selfiePreviewUrl} alt="" className="kyc-selfie-panel__oval-img" role="presentation" />
						) : null}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={`kyc-selfie-panel ${busy ? 'kyc-selfie-panel--disabled' : ''}`}>
			<p className="kyc-selfie-panel__intro">
				Please take a clear photo of your face. This helps us confirm your identity.
			</p>

			<div className="kyc-selfie-panel__stage">
				<div className="kyc-selfie-panel__oval">
					{showStaticSelfie ? (
						<div className="kyc-selfie-panel__oval-video">
							<img
								src={selfiePreviewUrl ?? ''}
								alt=""
								className="kyc-selfie-panel__video kyc-selfie-panel__video--static"
								role="presentation"
							/>
						</div>
					) : (
						<div className="kyc-selfie-panel__oval-video">
							<video ref={videoRef} className="kyc-selfie-panel__video" playsInline muted autoPlay hidden={!stream} />
						</div>
					)}
					{showPermission ? (
						<div className="kyc-selfie-panel__permission">
							<p className="kyc-selfie-panel__permission-text">Camera access is needed to take a selfie.</p>
							<button
								type="button"
								className="kyc-selfie-panel__allow-btn"
								disabled={busy || cameraLoading}
								onClick={() => {
									void requestCamera();
								}}
							>
								{cameraLoading ? 'Opening camera…' : 'Allow camera'}
							</button>
						</div>
					) : null}
				</div>
			</div>

			<div className="kyc-selfie-panel__hint" role="note">
				<ShieldIcon />
				<p>Make sure you&apos;re in a place with enough light to take a clear photo.</p>
			</div>

			{isRejected ? (
				<div className="kyc-selfie-panel__result kyc-selfie-panel__result--rejected" role="alert">
					{rejectionDisplay || 'Verification was not successful. Please try again.'}
				</div>
			) : null}

			<div className="kyc-selfie-panel__footer">
				{isRejected && onRetakeSelfie ? (
					<>
						<button
							type="button"
							className="kyc-selfie-panel__retake-btn"
							disabled={disabled || isProfileUploading || isVerifyingFace}
							onClick={() => {
								onRetakeSelfie();
							}}
						>
							Retake selfie
						</button>
						{onChangeDocument ? (
							<button
								type="button"
								className="kyc-selfie-panel__change-doc-btn"
								disabled={disabled || isProfileUploading || isVerifyingFace}
								onClick={() => {
									onChangeDocument();
								}}
							>
								<span className="kyc-selfie-panel__change-doc-icon" aria-hidden>
									<svg viewBox="0 0 24 24" width="18" height="18" focusable="false">
										<path
											fill="currentColor"
											d="M6 2h9l5 5v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm8 1.5V8h4.5L14 3.5zM7.5 12h9v1.5h-9V12zm0 3.5h9V17h-9v-1.5z"
										/>
									</svg>
								</span>
								<span>Change Document</span>
							</button>
						) : null}
					</>
				) : (
					<>
						<button
							type="button"
							className="kyc-selfie-panel__capture-btn"
							disabled={!stream || busy}
							onClick={captureAndUpload}
						>
							{isProfileUploading || isVerifyingFace ? 'Processing…' : 'Take a Selfie'}
						</button>
						{onChangeDocument ? (
							<button
								type="button"
								className="kyc-selfie-panel__change-doc-btn"
								disabled={busy}
								onClick={() => {
									onChangeDocument();
								}}
							>
								<span className="kyc-selfie-panel__change-doc-icon" aria-hidden>
									<svg viewBox="0 0 24 24" width="18" height="18" focusable="false">
										<path
											fill="currentColor"
											d="M6 2h9l5 5v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm8 1.5V8h4.5L14 3.5zM7.5 12h9v1.5h-9V12zm0 3.5h9V17h-9v-1.5z"
										/>
									</svg>
								</span>
								<span>Change Document</span>
							</button>
						) : null}
					</>
				)}
			</div>
			<canvas ref={canvasRef} className="kyc-selfie-panel__canvas" aria-hidden />
		</div>
	);
};
